import { call, select, put, all, take } from 'redux-saga/effects'
import { functions } from '../../../utilities/firebase'
import { localisedError } from '../../../utilities/Errors'
import that from '../../../utilities/translationHelper'
import getKitByKitId from '../../../dataservices/getKitByKitId'
import getItemByItemKey from '../../../dataservices/getItemByItemKey'
import getUserProfile from '../../../dataservices/getUserProfile'
import getUserProfileKitsCreated from '../../../dataservices/getUserProfileKitsCreated'
import getUserProfileKitsOwned from '../../../dataservices/getUserProfileKitsOwned'
import getUserProfileKitsWanted from '../../../dataservices/getUserProfileKitsWanted'
import getUserProfileItemsOwned from '../../../dataservices/getUserProfileItemsOwned'
import getUserProfileItemsWanted from '../../../dataservices/getUserProfileItemsWanted'
import getUserProfileFollowerIds from '../../../dataservices/getUserProfileFollowerIds'
import getUserProfileFollowingIds from '../../../dataservices/getUserProfileFollowingIds'
import actions from '../../actionCreators'
// eslint-disable-next-line import/no-named-as-default, temporary until we only export the one thing
import actionTypes from '../../actionTypes'
import { selectUserProfile, selectUid } from '../../selectors'
import { prepareForSave } from '../firebase/document'
import getUserProfilePostsCreated from '../../../dataservices/getUserProfilePostsCreated'
import getPostByPostId from '../../../dataservices/getPostByPostId'
import getUserProfileVerifierIds from '../../../dataservices/getUserProfileVerifierIds'
import getUserProfileVerifiedIds from '../../../dataservices/getUserProfileVerifiedIds'

export function* activateNewUser({ uid }) {
	const { data: result } = yield call(functions.httpsCallable('activateNewUser'), { uid })
	if (!result.success) {
		throw localisedError(that.context.translate('Could not activate user'), result)
	}
}

export function* getUserProfileToSave(params) {
	let userProfile = yield select(selectUserProfile)
	userProfile = yield call(prepareForSave, userProfile)
	return { ...params, userProfile }
}

export function* reserveUserName({ userProfile, meta }) {
	if (meta.reserveUserName) {
		const { data: result } = yield call(functions.httpsCallable('reserveUserName'), { userName: userProfile.userName })
		if (!result.success) {
			throw localisedError(that.context.translate('Username is in use. Please try a different username'), result)
		}
	}
}

export function maybeGoBack({ history, goBack }) {
	if (goBack) {
		history.goBack()
	}
}

export function* getParamsForFollowersSet(params) {
	const follower = yield call(prepareForSave, {})
	return {
		...params,
		follower
	}
}

export function* getParamsForFollowerRemove(params) {
	const follower = { uid: params.uid }
	const uid = yield select(selectUid)
	return {
		...params,
		uid,
		follower
	}
}

export function* getParamsForVerifiersSet(params) {
	const verifier = yield call(prepareForSave, {
		relationshipType: params.relationshipType
	})
	return {
		...params,
		verifier
	}
}

export function* getParamsForFetchItems(params) {
	const userProfile = yield select(selectUserProfile)
	return {
		...params,
		userProfile
	}
}

export function* getPostsByPostsCreated(postsCreatedDocs = []) {
	const results = yield all(postsCreatedDocs.map(function* get(postsCreated) {
		try {
			return yield call(getPostByPostId, { postId: postsCreated.postId })
		} catch (error) {
			if (error.details && error.details[0] && error.details[0].reason === 'deleted') {
				return yield put(actions.fetchWarning(error))
			}
			return yield put(actions.fetchFatalError(error)) // this will have no post member
		}
	}))
	return results.filter(result => result.post).map(result => result.post)
}

export function* getKitsByKitsCreated(kitsCreatedDocs = []) {
	const results = yield all(kitsCreatedDocs.map(function* get(kitsCreated) {
		try {
			return yield call(getKitByKitId, { kitId: kitsCreated.kitId })
		} catch (error) {
			if (error.details && error.details[0] && error.details[0].reason === 'deleted') {
				return yield put(actions.fetchWarning(error))
			}
			return yield put(actions.fetchFatalError(error)) // this will have no kit member
		}
	}))
	return results.filter(result => result.kit).map(result => result.kit)
}

export function* getKitsByKitOwnedWanted(kitOwnedWantedDocs = []) {
	const results = yield all(kitOwnedWantedDocs.map(function* get(kitOwnedWanted) {
		try {
			return yield call(getKitByKitId, { kitId: kitOwnedWanted.kitId })
		} catch (error) {
			if (error.details && error.details[0] && error.details[0].reason === 'deleted') {
				return yield put(actions.fetchWarning(error))
			}
			return yield put(actions.fetchFatalError(error)) // this will have no kit member
		}
	}))
	return results.filter(result => result.kit).map(result => result.kit)
}

export function* getItemsByItemOwnedWanted(itemOwnedWantedDocs = []) {
	const results = yield all(itemOwnedWantedDocs.map(function* get(itemOwnedWanted) {
		try {
			const result = yield call(getItemByItemKey, { itemKey: itemOwnedWanted.itemKey })
			result.colourNames = itemOwnedWanted.colourNames
			return result
		} catch (error) {
			if (error.details && error.details[0] && error.details[0].reason === 'deleted') {
				return yield put(actions.fetchWarning(error))
			}
			return yield put(actions.fetchFatalError(error)) // this will have no item member
		}
	}))
	return results
		.filter(result => result.item)
		.reduce((items, result) => [
			...items,
			...Object.keys(result.colourNames).map(colourName => ({
				...result.item,
				colourName
			}))
		], [])
}

export function* getPosts({ userProfile }) {
	const postsCreatedDocs = yield call(getUserProfilePostsCreated, userProfile)
	const posts = yield call(getPostsByPostsCreated, postsCreatedDocs)
	return { posts }
}

export function* getKits({ userProfile }) {
	const kitsCreatedDocs = yield call(getUserProfileKitsCreated, userProfile)
	const kits = yield call(getKitsByKitsCreated, kitsCreatedDocs)
	return { kits }
}

export function* getKitsOwned({ userProfile }) {
	const kitOwnedDocs = yield call(getUserProfileKitsOwned, userProfile)
	const kitsOwned = yield call(getKitsByKitOwnedWanted, kitOwnedDocs)
	return { kitsOwned }
}

export function* getKitsWanted({ userProfile }) {
	const kitWantedDocs = yield call(getUserProfileKitsWanted, userProfile)
	const kitsWanted = yield call(getKitsByKitOwnedWanted, kitWantedDocs)
	return { kitsWanted }
}

export function* getItemsOwned({ userProfile }) {
	const itemOwnedDocs = yield call(getUserProfileItemsOwned, userProfile)
	const itemsOwned = yield call(getItemsByItemOwnedWanted, itemOwnedDocs)
	return { itemsOwned }
}

export function* getItemsWanted({ userProfile }) {
	const itemWantedDocs = yield call(getUserProfileItemsWanted, userProfile)
	const itemsWanted = yield call(getItemsByItemOwnedWanted, itemWantedDocs)
	return { itemsWanted }
}

export function* getUserProfilesByIds(ids = []) {
	const ups = []
	for (const uid of ids) { // eslint-disable-line no-restricted-syntax
		try {
			const result = yield call(getUserProfile, { uid, fromPublic: true })
			ups.push(result.userProfile)
		} catch (error) {
			if (error.details && error.details[0] && error.details[0].reason === 'deleted') {
				return yield put(actions.fetchWarning(error))
			}
			yield put(actions.fetchFatalError(error))
		}
	}
	return ups
}

export function* getFollowers({ userProfile }) {
	const ids = yield call(getUserProfileFollowerIds, userProfile)
	const followers = yield call(getUserProfilesByIds, ids)
	return { followers }
}

export function* getFollowing({ userProfile }) {
	const ids = yield call(getUserProfileFollowingIds, userProfile)
	const following = yield call(getUserProfilesByIds, ids)
	return { following }
}

export function* getVerifiers({ userProfile }) {
	const ids = yield call(getUserProfileVerifierIds, userProfile)
	const verifiers = yield call(getUserProfilesByIds, ids)
	return { verifiers }
}

export function* getVerified({ userProfile }) {
	const ids = yield call(getUserProfileVerifiedIds, userProfile)
	const verified = yield call(getUserProfilesByIds, ids)
	return { verified }
}

export function* sendWelcomeEmail() {
	// wait for the save to complete
	yield take(actionTypes.userProfileSave_Success)
	yield call(functions.httpsCallable('usersSendWelcomeEmail'))
}



// WEBPACK FOOTER //
// ./src/redux/saga/userProfile/sagas.js